import { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePolling } from '../../video/hooks/usePolling';
import { cancelStressTest, fetchStressAnalytics, fetchStressTest } from '../api';
import { RetriesPanel } from '../components/RetriesPanel';
import type { StressAnalytics, StressTest } from '../types';
import { isStressTerminal } from '../types';
import {
  ErrorGroupsTable,
  LatencyOverTimeChart,
  ModelBreakdownTable,
  OutcomeChart,
  PercentileChart,
  SlowestTable,
  ThroughputChart,
} from './StressAnalyticsPanels';
import {
  RANDOM_MODEL_TYPE,
  endpointLabel,
  formatDuration,
  formatSeconds,
  formatTimestamp,
  statusClasses,
} from './format';

/** Fast enough to feel live, slow enough not to add load to the test itself. */
const LIVE_POLL_MS = 3000;
/** A finished run can't change, so back right off once it's terminal. */
const IDLE_POLL_MS = 60_000;

export function StressDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [finished, setFinished] = useState(false);

  const pollMs = finished ? IDLE_POLL_MS : LIVE_POLL_MS;

  const { data: test, error, refresh } = usePolling<StressTest>(
    async () => {
      const result = await fetchStressTest(id);
      // Keep polling for one more cycle after the runner stops: calls submitted
      // in the final burst are still landing in `api_calls`.
      setFinished(isStressTerminal(result.status) && Boolean(result.finished_at));
      return result;
    },
    pollMs,
    [id],
  );

  const { data: analytics } = usePolling<StressAnalytics>(
    () => fetchStressAnalytics(id, 10),
    pollMs,
    [id],
  );

  const handleCancel = useCallback(async () => {
    setCancelling(true);
    setCancelError(null);
    try {
      await cancelStressTest(id);
      refresh();
    } catch (e) {
      setCancelError(e instanceof Error ? e.message : String(e));
    } finally {
      setCancelling(false);
    }
  }, [id, refresh]);

  if (error && !test) {
    return (
      <div className="max-w-6xl mx-auto">
        <BackLink />
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm">
          {error}
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="max-w-6xl mx-auto">
        <BackLink />
        <div className="text-sm text-slate-500">Loading stress test…</div>
      </div>
    );
  }

  const live = !isStressTerminal(test.status);
  const config = test.config || {};
  const submitProgress = test.calls_planned
    ? ((test.calls_submitted + test.calls_failed_to_submit) / test.calls_planned) * 100
    : 0;

  return (
    <div className="max-w-6xl mx-auto">
      <BackLink />

      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div>
          <div className="flex items-center gap-2.5">
            <h1 className="text-2xl font-semibold text-slate-900">
              {test.name || `Test ${test.id.slice(0, 8)}`}
            </h1>
            <span
              className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusClasses(test.status)}`}
            >
              {test.status}
            </span>
            {live && (
              <span className="flex items-center gap-1.5 text-xs text-indigo-600">
                <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 animate-pulse" />
                live
              </span>
            )}
          </div>
          <p className="text-sm text-slate-500 mt-1">
            {config.total_calls} calls{' '}
            {config.model_type === RANDOM_MODEL_TYPE
              ? 'across every endpoint'
              : `at ${endpointLabel(config.model_type || '')}`}{' '}
            in bursts of {config.burst_size}, {config.burst_delay_seconds}s apart ·{' '}
            {config.image_width}&times;{config.image_height} inputs ·{' '}
            {config.wait ? 'synchronous' : 'async'} submits
          </p>
        </div>
        {live && (
          <button
            onClick={handleCancel}
            disabled={cancelling}
            className="shrink-0 px-4 py-2 rounded-lg border border-danger/40 text-danger text-sm font-medium hover:bg-danger/5 disabled:opacity-50 transition-colors"
          >
            {cancelling ? 'Cancelling…' : 'Cancel test'}
          </button>
        )}
      </div>

      {cancelError && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm mb-4">
          {cancelError}
        </div>
      )}
      {test.error && (
        <div className="rounded-lg border border-danger/30 bg-danger/5 text-danger px-4 py-3 text-sm mb-4">
          The run failed: {test.error}
        </div>
      )}

      {/* ── Submission progress ─────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-5 mb-4">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-slate-700">Submission</span>
          <span className="text-slate-500 tabular-nums">
            {test.calls_submitted + test.calls_failed_to_submit} / {test.calls_planned} calls ·{' '}
            {test.bursts_completed} burst{test.bursts_completed === 1 ? '' : 's'}
          </span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
          <div
            className="h-full bg-indigo-500 transition-all duration-500"
            style={{ width: `${Math.min(100, submitProgress)}%` }}
          />
        </div>
        {test.calls_failed_to_submit > 0 && (
          <p className="mt-2 text-xs text-danger">
            {test.calls_failed_to_submit} call
            {test.calls_failed_to_submit === 1 ? ' was' : 's were'} rejected before they reached the
            provider — the service refused them at the HTTP layer.
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Stat label="Duration" value={formatDuration(test.started_at, test.finished_at)} />
        <Stat
          label="Throughput"
          value={
            analytics?.totals.throughput_per_min != null
              ? `${analytics.totals.throughput_per_min.toFixed(1)} / min`
              : '—'
          }
        />
        <Stat
          label="Success rate"
          value={
            analytics && analytics.totals.total > 0
              ? `${((analytics.totals.success / analytics.totals.total) * 100).toFixed(0)}%`
              : '—'
          }
        />
        <Stat label="p90 end to end" value={formatSeconds(analytics?.totals.total_p90)} />
      </div>

      <LightningBatchPanel test={test} />

      {analytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <ThroughputChart analytics={analytics} />
          <LatencyOverTimeChart analytics={analytics} />
          <PercentileChart analytics={analytics} />
          <OutcomeChart analytics={analytics} />
          <ModelBreakdownTable analytics={analytics} />
          <ErrorGroupsTable analytics={analytics} />
          <SlowestTable analytics={analytics} />
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-soft p-5">
          <h3 className="text-sm font-semibold text-slate-900">Run details</h3>
          <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <Detail label="Test id" value={test.id} mono />
            <Detail label="Created" value={formatTimestamp(test.created_at)} />
            <Detail label="Started" value={formatTimestamp(test.started_at)} />
            <Detail label="Finished" value={formatTimestamp(test.finished_at)} />
            <Detail label="Scans in CSV" value={String(test.scan_ids?.length ?? 0)} />
            <Detail label="CSV" value={test.filename || '—'} />
            <Detail
              label="Cost recorded"
              value={
                analytics ? `$${analytics.totals.cost_usd.toFixed(4)} (dummy mode)` : '—'
              }
            />
            <Detail label="Cold boots" value={String(analytics?.totals.cold_boots ?? 0)} />
          </dl>
        </div>
        <RetriesPanel
          groups={analytics?.retry_groups}
          loading={!analytics}
          emptyLabel="No finished calls with a retry count yet"
        />
      </div>
    </div>
  );
}

function BackLink() {
  return (
    <Link
      to="/gen-api/stress"
      className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors"
    >
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
      </svg>
      All stress tests
    </Link>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-soft px-4 py-3">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-lg font-semibold text-slate-900 tabular-nums mt-0.5">{value}</div>
    </div>
  );
}

function Detail({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4 py-1 border-b border-slate-50">
      <dt className="text-slate-500">{label}</dt>
      <dd className={`text-slate-800 text-right ${mono ? 'font-mono text-xs' : ''}`}>{value}</dd>
    </div>
  );
}

/**
 * The lightning half of the run. Lightning owns this batch entirely, so the
 * numbers are proxied live from its own API rather than mirrored into apigen.
 */
function LightningBatchPanel({ test }: { test: StressTest }) {
  if (!test.config?.trigger_lightning_batch) return null;

  if (test.lightning_error) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-5 py-4">
        <h3 className="text-sm font-semibold text-amber-900">Lightning batch skipped</h3>
        <p className="text-xs text-amber-800 mt-1">{test.lightning_error}</p>
        <p className="text-xs text-amber-700 mt-1">
          The gen-api half of this test ran normally.
        </p>
      </div>
    );
  }

  if (!test.lightning_batch_id) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-5">
        <h3 className="text-sm font-semibold text-slate-900">Lightning batch</h3>
        <p className="text-xs text-slate-500 mt-1">Waiting for the batch to be accepted…</p>
      </div>
    );
  }

  const batch = test.lightning_batch;
  const total = batch?.total_scans ?? test.lightning_total_scans ?? 0;
  const done = (batch?.completed ?? 0) + (batch?.failed ?? 0);
  const percent = total > 0 ? (done / total) * 100 : 0;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-soft p-5">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900">Lightning batch</h3>
        <span className="font-mono text-xs text-slate-400">
          {test.lightning_batch_id.slice(0, 8)}
        </span>
      </div>
      {!batch ? (
        <p className="text-xs text-slate-500 mt-1">
          Batch accepted, but lightning didn&apos;t return its progress — it may be unreachable from
          apigen right now.
        </p>
      ) : (
        <>
          <div className="flex items-center justify-between text-sm mt-2">
            <span className="text-slate-500">
              {batch.finished ? 'Finished' : 'Running'} · {batch.completed} done, {batch.failed}{' '}
              failed
              {batch.in_progress != null && `, ${batch.in_progress} in progress`}
            </span>
            <span className="text-slate-500 tabular-nums">
              {done} / {total} scans
            </span>
          </div>
          <div className="mt-2 h-2 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${Math.min(100, percent)}%` }}
            />
          </div>
        </>
      )}
    </div>
  );
}
